/**
 * 模块: 品牌竞争关系表格
 * 职责: 展示品牌间竞争关系详情
 * 限制: 文件<280行
 */

import React from 'react';
import { Card, Table, Tag, Typography } from 'antd';
import type { Edge } from './types';

const { Text } = Typography;

interface CompetitionTableProps {
  edges: Edge[];
}

export const CompetitionTable: React.FC<CompetitionTableProps> = ({ edges }) => {
  const getTableData = () => {
    return edges.map((edge, index) => ({
      key: index,
      source: edge.source,
      target: edge.target,
      frequency: edge.weight,
      type: edge.type,
      shared_keywords_count: edge.shared_keywords.length,
      shared_keywords: edge.shared_keywords,
    }));
  };

  const columns = [
    {
      title: '品牌A',
      dataIndex: 'source',
      key: 'source',
      width: 150,
    },
    {
      title: '品牌B',
      dataIndex: 'target',
      key: 'target',
      width: 150,
    },
    {
      title: '竞争频率',
      dataIndex: 'frequency',
      key: 'frequency',
      width: 100,
      sorter: (a: any, b: any) => a.frequency - b.frequency,
      render: (value: number) => <Tag color="red">{value}</Tag>,
    },
    {
      title: '竞争类型',
      dataIndex: 'type',
      key: 'type',
      width: 120,
      render: (type: string) => {
        const colors: { [key: string]: string } = {
          'direct': 'red',
          'indirect': 'orange',
          'potential': 'green',
        };
        const texts: { [key: string]: string } = {
          'direct': '直接竞争',
          'indirect': '间接竞争',
          'potential': '潜在竞争',
        };
        return <Tag color={colors[type]}>{texts[type]}</Tag>;
      },
    },
    {
      title: '共同关键词数',
      dataIndex: 'shared_keywords_count',
      key: 'shared_keywords_count',
      width: 130,
      sorter: (a: any, b: any) => a.shared_keywords_count - b.shared_keywords_count,
    },
    {
      title: '关键词示例',
      dataIndex: 'shared_keywords',
      key: 'shared_keywords',
      ellipsis: true,
      render: (keywords: string[]) => keywords.join(', ') || '-',
    },
  ];

  if (!edges || edges.length === 0) {
    return (
      <Card 
        style={{ marginTop: 16 }} 
        title="竞争关系详情 (0 条)"
      >
        <div style={{ textAlign: 'center', padding: '40px 0' }}>
          <Text type="secondary">
            暂无竞争关系数据。可能原因：
            <br />
            1. 当前筛选条件下没有符合的竞争关系
            <br />
            2. 尝试降低"最小竞争频率"阈值
            <br />
            3. 点击"生成网络"按钮重新生成分析数据
          </Text>
        </div>
      </Card>
    );
  }

  return (
    <Card 
      style={{ marginTop: 16 }} 
      title={`竞争关系详情 (${edges.length} 条)`}
    >
      <Table
        columns={columns}
        dataSource={getTableData()}
        pagination={{
          pageSize: 20,
          showSizeChanger: true,
          pageSizeOptions: ['10', '20', '50', '100'],
          showTotal: (total) => `共 ${total} 条竞争关系`,
        }}
        size="small"
      />
    </Card>
  );
};

